import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { profileStore } from '../stores/profileStore'
import { leaderboardStore } from '../stores/leaderboardStore'
import { gamificationStore } from '../stores/gamificationStore'
import { mojilandStore } from '../stores/mojilandStore'
import { pickRandom } from '../utils/helpers'
import { findLevels } from '../utils/levels'
import { resources } from '../utils/sets'
import { playSound, vibrate } from '../utils/sounds'
import GameLeaderboard from '../components/GameLeaderboard'

const TIME_LIMIT = 30
const EMOJI_COUNT = 80 // More emojis scattered around

interface EmojiItem {
  id: number
  emoji: string
  x: number
  y: number
  size: number
  rotation: number
  zIndex: number
  opacity: number
}

interface FloatingScore {
  id: number
  x: number
  y: number
  score: number
  isBonus: boolean
}

const FindMoji = observer(() => {
  const { t } = useTranslation()
  const [level, setLevel] = useState(0)
  const [emojis, setEmojis] = useState<EmojiItem[]>([])
  const [target, setTarget] = useState('')
  const [score, setScore] = useState(0)
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT)
  const [isPlaying, setIsPlaying] = useState(false)
  const [gameOver, setGameOver] = useState(false)
  const [streak, setStreak] = useState(0)
  const [maxStreak, setMaxStreak] = useState(0)
  const [found, setFound] = useState(0)
  const [wrongClick, setWrongClick] = useState<number | null>(null)
  const [resourcesEarned, setResourcesEarned] = useState<Record<string, number>>({})
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [floatingScores, setFloatingScores] = useState<FloatingScore[]>([])
  const [lastFoundId, setLastFoundId] = useState<number | null>(null)
  const [targetCount, setTargetCount] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const sound = (type: Parameters<typeof playSound>[0]) => {
    if (soundEnabled) playSound(type)
  }

  // Generate random positions for emojis
  const generateEmojis = useCallback((lvl: number) => {
    const set = findLevels[lvl] || findLevels[0]
    const newTarget = pickRandom(set)
    
    // Random number of targets (16-24 to make it challenging)
    const numTargets = 16 + Math.floor(Math.random() * 9)
    setTargetCount(numTargets)
    
    const newEmojis: EmojiItem[] = []
    
    // Place emojis with random positions, sizes, and rotations
    for (let i = 0; i < EMOJI_COUNT; i++) {
      const isTarget = i < numTargets
      const emoji = isTarget ? newTarget : pickRandom(set.filter(e => e !== newTarget))
      
      // Random position with some padding from edges
      const x = 5 + Math.random() * 90
      const y = 5 + Math.random() * 90
      
      // Random size (smaller = harder to find)
      const size = 20 + Math.random() * 24
      
      // Random rotation for visual variety
      const rotation = -25 + Math.random() * 50
      
      // Random z-index for overlapping
      const zIndex = Math.floor(Math.random() * 10)
      
      // Slight opacity variation
      const opacity = 0.85 + Math.random() * 0.15
      
      newEmojis.push({
        id: i,
        emoji,
        x,
        y,
        size,
        rotation,
        zIndex,
        opacity
      })
    }
    
    // Shuffle to mix targets with distractors
    const shuffled = newEmojis.sort(() => Math.random() - 0.5)
    
    setEmojis(shuffled)
    setTarget(newTarget)
  }, [])

  // Start game
  const startGame = useCallback(() => {
    sound('go')
    setScore(0)
    setTimeLeft(TIME_LIMIT)
    setIsPlaying(true)
    setGameOver(false)
    setStreak(0)
    setMaxStreak(0)
    setFound(0)
    setResourcesEarned({})
    setFloatingScores([])
    generateEmojis(level)
  }, [level, generateEmojis, soundEnabled])

  // Timer
  useEffect(() => {
    if (!isPlaying || timeLeft <= 0) {
      if (timeLeft <= 0 && isPlaying) {
        setIsPlaying(false)
        setGameOver(true)
        sound('lose')
        vibrate(200)
        
        const earned: Record<string, number> = {}
        const resourceReward = Math.floor(score / 15) + level + 1 + Math.floor(maxStreak / 2)
        resources.forEach(r => {
          earned[r] = Math.floor(resourceReward / resources.length) + (Math.random() > 0.5 ? 1 : 0)
        })
        setResourcesEarned(earned)
        
        Object.entries(earned).forEach(([icon, amount]) => {
          profileStore.addResource(icon, amount)
        })
        
        // Submit score to leaderboard
        if (score > 0) {
          leaderboardStore.submitGameScore('findmoji', score, level)
          gamificationStore.recordGameResult('findmoji', score, level, false)
          mojilandStore.tryCompleteFromGame('findmoji', score, level, false)
        }
      }
      return
    }

    const timer = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 5 && t > 0) sound('tick')
        return t - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isPlaying, timeLeft, score, level, maxStreak, soundEnabled])

  // Add floating score animation
  const addFloatingScore = (x: number, y: number, points: number, isBonus: boolean) => {
    const id = Date.now() + Math.random()
    
    setFloatingScores(prev => [...prev, { id, x, y, score: points, isBonus }])
    
    setTimeout(() => {
      setFloatingScores(prev => prev.filter(f => f.id !== id))
    }, 800)
  }

  // Handle emoji click
  const handleEmojiClick = (item: EmojiItem) => {
    if (!isPlaying) return

    if (item.emoji === target) {
      const newStreak = streak + 1
      const bonus = newStreak > 5 ? 3 : newStreak > 2 ? 2 : 1
      const points = 10 * bonus
      
      if (newStreak > 5) {
        sound('superCombo')
        vibrate([30, 20, 30])
      } else if (newStreak > 2) {
        sound('combo')
        vibrate(30)
      } else {
        sound('match')
        vibrate(20)
      }
      
      setScore(s => s + points)
      setStreak(newStreak)
      setFound(f => f + 1)
      if (newStreak > maxStreak) setMaxStreak(newStreak)
      
      setLastFoundId(item.id)
      setTimeout(() => setLastFoundId(null), 300)
      
      addFloatingScore(item.x, item.y, points, bonus > 1)
      
      // Remove found emoji and check if we need more targets
      setEmojis(prev => {
        const remaining = prev.filter(e => e.id !== item.id)
        const remainingTargets = remaining.filter(e => e.emoji === target)
        
        // If few targets left, regenerate
        if (remainingTargets.length < 5) {
          setTimeout(() => generateEmojis(level), 200)
        }
        
        return remaining
      })
    } else {
      sound('wrong')
      vibrate(100)
      setStreak(0)
      setTimeLeft(t => Math.max(0, t - 2))
      setWrongClick(item.id)
      setTimeout(() => setWrongClick(null), 300)
    }
  }

  const handleLevelSelect = (newLevel: number) => {
    sound('click')
    setLevel(newLevel)
    setIsPlaying(false)
    setGameOver(false)
  }

  const timePercent = (timeLeft / TIME_LIMIT) * 100
  const currentSet = findLevels[level] || findLevels[0]
  const remainingTargets = emojis.filter(e => e.emoji === target).length

  return (
    <div className="min-h-screen findmoji-bg flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 game-header">
        <div className="max-w-4xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link 
                to="/"
                className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70 text-gray-700"
              >
                <span className="text-sm">←</span>
              </Link>
              <img src="/logo.png" alt="PlanetMoji" className="w-7 h-7 drop-shadow-lg" />
              <span className="font-title text-base text-gray-800">FindMoji</span>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70"
              >
                <span>{soundEnabled ? '🔊' : '🔇'}</span>
              </button>
            </div>
          </div>
          
          {/* Game Stats */}
          {isPlaying && (
            <>
              <div className="flex justify-between items-center mt-2 gap-2">
                <div className="flex items-center gap-3 glass-pill">
                  <span className="text-4xl animate-pulse">{target}</span>
                  <div>
                    <span className="text-gray-800 font-bold">Find me!</span>
                    <p className="text-gray-500 text-xs">{remainingTargets} missing</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`glass-pill text-sm font-bold ${timeLeft <= 5 ? 'bg-red-500/50 animate-pulse text-white' : ''}`}>
                    <span>⏱️</span>
                    <span className={timeLeft <= 5 ? 'text-white ml-1' : 'text-gray-800 ml-1'}>{timeLeft}s</span>
                  </div>
                  <div className="glass-pill text-sm font-bold">
                    <span>🔥</span>
                    <span className="text-gray-800 ml-1">{score}</span>
                  </div>
                </div>
              </div>
              
              {/* Streak indicator */}
              {streak > 0 && (
                <div className="flex justify-center mt-2">
                  <div className={`px-3 py-1 rounded-full text-sm font-bold text-white ${
                    streak > 5 ? 'bg-gradient-to-r from-yellow-400 to-orange-500 animate-pulse' :
                    streak > 2 ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                    'bg-indigo-500/70'
                  }`}>
                    <span>🔥 {streak} streak!</span>
                    {streak > 2 && <span className="ml-1">×{streak > 5 ? 3 : 2}</span>}
                  </div>
                </div>
              )}
              
              {/* Time bar */}
              <div className="mt-2 h-2 bg-white/40 rounded-full overflow-hidden border border-white/50">
                <div 
                  className={`h-full transition-all duration-1000 ${timeLeft <= 5 ? 'bg-red-400' : 'bg-emerald-400'}`}
                  style={{ width: `${timePercent}%` }}
                />
              </div>
            </>
          )}
        </div>
      </header>
      
      <div className="flex-1 relative overflow-hidden" ref={containerRef}>
        {!isPlaying && !gameOver ? (
          // Start screen
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="glass-card p-8 max-w-md w-full">
              <div className="text-center">
                <span className="text-6xl block mb-4">🔍</span>
                <h2 className="font-title text-2xl text-gray-800 mb-2">{t('common.levelN', { n: level + 1 })}</h2>
                <div className="flex justify-center gap-2 mb-4 flex-wrap">
                  {currentSet.slice(0, 8).map((emoji, i) => (
                    <span key={i} className="text-3xl">{emoji}</span>
                  ))}
                </div>
                <p className="text-gray-500 text-sm mb-6">
                  {t('findmoji.instructions')}
                </p>
                <button
                  onClick={startGame}
                  className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-2xl font-bold text-lg hover:scale-105 transition-transform shadow-lg shadow-green-500/30"
                >
                  🎮 {t('findmoji.startGame')}
                </button>
              </div>
            </div>
          </div>
        ) : isPlaying ? (
          // Game area - scattered emojis
          <div className="absolute inset-0">
            {/* Floating Scores */}
            {floatingScores.map(fs => (
              <div
                key={fs.id}
                className="absolute pointer-events-none z-50 animate-float-up"
                style={{ left: `${fs.x}%`, top: `${fs.y}%` }}
              >
                <div className={`px-2 py-1 rounded-full font-bold text-sm shadow-lg ${
                  fs.isBonus ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white' : 'bg-white text-gray-800'
                }`}>
                  +{fs.score}
                </div>
              </div>
            ))}
            
            {/* Scattered emojis */}
            {emojis.map((item) => (
              <button
                key={item.id}
                onClick={() => handleEmojiClick(item)}
                className={`
                  absolute transition-all duration-150 select-none
                  hover:scale-125 hover:z-50 active:scale-90
                  ${wrongClick === item.id ? 'animate-shake scale-75 opacity-50' : ''}
                  ${lastFoundId === item.id ? 'scale-150 opacity-0' : ''}
                `}
                style={{
                  left: `${item.x}%`,
                  top: `${item.y}%`,
                  fontSize: `${item.size}px`,
                  transform: `rotate(${item.rotation}deg)`,
                  zIndex: item.zIndex,
                  opacity: lastFoundId === item.id ? 0 : item.opacity,
                  textShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              >
                {item.emoji}
              </button>
            ))}
          </div>
        ) : null}

        {/* Level selector - always visible at bottom */}
        <div className="absolute bottom-4 left-4 right-4 z-40">
          <div className="glass-card p-3 max-w-md mx-auto">
            <p className="text-center text-white/40 text-xs mb-2">{t('memoji.selectLevel')}</p>
            <div className="flex justify-center gap-1.5 flex-wrap">
              {findLevels.map((set, idx) => {
                const isActive = idx === level
                return (
                  <button
                    key={idx}
                    onClick={() => handleLevelSelect(idx)}
                    className={`
                      w-9 h-9 rounded-lg flex items-center justify-center text-lg
                      transition-all duration-150 cursor-pointer
                      ${isActive 
                        ? 'bg-white scale-110 shadow-md' 
                        : 'bg-white/10 hover:bg-white/25'
                      }
                    `}
                  >
                    {set[0]}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Mini Leaderboard - shown when not playing */}
        {!isPlaying && !gameOver && (
          <div className="absolute top-4 right-4 w-64 z-30 hidden sm:block">
            <GameLeaderboard 
              game="findmoji"
              gameName="FindMoji"
              emoji="🔍"
              currentScore={score}
              gradient="from-emerald-500 to-green-500"
              limit={5}
            />
          </div>
        )}

        {/* Game over modal */}
        {gameOver && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl animate-bounce-in">
              <span className="text-6xl block mb-3">⏰</span>
              <h2 className="font-title text-2xl text-emerald-600 mb-1">{t('findmoji.timesUp')}</h2>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 my-4">
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-emerald-500">{score}</p>
                  <p className="text-xs text-gray-500">{t('common.score')}</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-orange-500">{maxStreak}</p>
                  <p className="text-xs text-gray-500">{t('findmoji.bestStreak')}</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-purple-500">{found}</p>
                  <p className="text-xs text-gray-500">{t('findmoji.found')}</p>
                </div>
              </div>
              
              {/* Resources */}
              {Object.keys(resourcesEarned).length > 0 && score > 0 && (
                <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-2">Resources earned:</p>
                  <div className="flex justify-center gap-3">
                    {Object.entries(resourcesEarned).map(([icon, amount]) => (
                      <div key={icon} className="flex items-center gap-1">
                        <span className="text-xl">{icon}</span>
                        <span className="font-bold text-green-600">+{amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 justify-center">
                <button
                  onClick={startGame}
                  className="px-5 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200"
                >
                  🔄 {t('common.retry')}
                </button>
                {level < findLevels.length - 1 && (
                  <button
                    onClick={() => {
                      sound('levelUp')
                      setLevel(level + 1)
                      setGameOver(false)
                    }}
                    className="px-5 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-semibold"
                  >
                    {t('common.nextLevel')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes float-up {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-60px) scale(1.3); }
        }
        .animate-float-up {
          animation: float-up 0.8s ease-out forwards;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0) rotate(0); }
          25% { transform: translateX(-6px) rotate(-5deg); }
          75% { transform: translateX(6px) rotate(5deg); }
        }
        .animate-shake {
          animation: shake 0.2s ease-in-out 2;
        }
      `}</style>
    </div>
  )
})

export default FindMoji
